<template>

	<AppTable
		ref="products-table"
		:table-headings="tableHeadingsProducts"
		:title="getString('products')"
		:total-items="parsedProducts.products.length"
		:items="currentShownProducts"
		:filters="filtersProducts"
		:main-color="'#0752A3'"
		@changed-page="v=>currentPageProducts = v"
		@changed-page-size="v=>pageSizeProducts = v"
		@changed-sort="changeSortProducts"
		@changed-filter="changeFilterProducts"
		allow-download
		@export="exportProducts"
		:info-button="getString('info_table_products')"
		class="mb-30"
	/>

	<AppTable
		ref="categories-table"
		:table-headings="tableHeadingsCategories"
		:title="getString('categories')"
		:total-items="parsedProducts.categories.length"
		:items="currentShownCategories"
		:filters="filtersCategories"
		:main-color="'#0752A3'"
		@changed-page="v=>currentPageCategories = v"
		@changed-page-size="v=>pageSizeCategories = v"
		@changed-sort="changeSortCategories"
		@changed-filter="changeFilterCategories"
		allow-download
		@export="exportCategories"
		:info-button="getString('info_table_categories')"
		class="mb-30"
	/>

	<AppTable
		ref="variations-table"
		:table-headings="tableHeadingsVariations"
		:title="getString('variations')"
		:total-items="parsedProducts.variations.length"
		:items="currentShownVariations"
		:filters="filtersVariations"
		:main-color="'#0752A3'"
		@changed-page="v=>currentPageVariations = v"
		@changed-page-size="v=>pageSizeVariations = v"
		@changed-sort="changeSortVariations"
		@changed-filter="changeFilterVariations"
		allow-download
		@export="exportVariations"
		:info-button="getString('info_table_variations')"
		class="mb-30"
	/>

	<AppTable
		ref="vat-table"
		:table-headings="tableHeadingsVat"
		:title="getString('vat_breakdown')"
		:total-items="parsedProducts.vat.length"
		:items="currentShownVat"
		:filters="filtersVat"
		:main-color="'#0752A3'"
		@changed-page="v=>currentPageVat = v"
		@changed-page-size="v=>pageSizeVat = v"
		@changed-sort="changeSortVat"
		@changed-filter="changeFilterVat"
		allow-download
		@export="exportVat"
		class="mb-30"
	/>

</template>

<script>
import {useCurrencyStore, useLanguageStore, useScriptStore, useNotificationStore} from "@/shared/stores";
import {useAnalyticsStore, useEpsonStore, useMenuStore} from "@/console-new/stores";
import AppTable from "@/shared/components/AppTable.vue";
import {formatDateOnly} from "@/shared/helpers/dates";
import {useUserStore} from "@/user/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		const scriptStore = useScriptStore();
		const notificationStore = useNotificationStore();
		const epsonStore = useEpsonStore();
		return {analyticsStore, currencyStore, getString: languageStore.getString, userStore, scriptStore, showNotification: notificationStore.showNotification, epsonStore};
	},
	name: "ProductsTablesAggregated",
	components: {AppTable},
	props: {
		dates: {
			type: Object,
			required: true,
			validator: (value) => {
				return value.hasOwnProperty("start") && value.hasOwnProperty("end") && value.hasOwnProperty("previousStart") && value.hasOwnProperty("previousEnd") && value.hasOwnProperty("timeframeIndex");
			}
		},
	},
	data() {
		return {
			// Products table configuration
			tableHeadingsProducts: [
				{
					text: this.getString('product'),
					fieldName: 'name',
					sortable: true,
					cols: 3,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('net_volume'),
					fieldName: 'readable_net_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('units_sold'),
					fieldName: 'units_sold',
					sortable: true,
					cols: 1,
				},
			],
			filtersProducts: [
				{
					type: "quantity",
					key: "units_sold",
					text: this.getString('units_sold'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "net_volume",
					text: this.getString('net_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			pageSizeProducts: 10,
			currentPageProducts: 1,
			sortFieldProducts: "readable_gross_volume",
			sortAscProducts: null,
			selectedFiltersProducts: null,
			
			// Categories table configuration
			tableHeadingsCategories: [
				{
					text: this.getString('category'),
					fieldName: 'name',
					sortable: true,
					cols: 3,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('net_volume'),
					fieldName: 'readable_net_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('units_sold'),
					fieldName: 'units_sold',
					sortable: true,
					cols: 1,
				},
			],
			filtersCategories: [
				{
					type: "quantity",
					key: "units_sold",
					text: this.getString('units_sold'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "net_volume",
					text: this.getString('net_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			pageSizeCategories: 10,
			currentPageCategories: 1,
			sortFieldCategories: "readable_gross_volume",
			sortAscCategories: null,
			selectedFiltersCategories: null,
			
			// Variations table configuration
			tableHeadingsVariations: [
				{
					text: this.getString('variation'),
					fieldName: 'name',
					sortable: true,
					cols: 3,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 2,
				},
				{
					text: this.getString('units_sold'),
					fieldName: 'units_sold',
					sortable: true,
					cols: 1,
				},
			],
			filtersVariations: [
				{
					type: "quantity",
					key: "units_sold",
					text: this.getString('units_sold'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			pageSizeVariations: 10,
			currentPageVariations: 1,
			sortFieldVariations: "readable_gross_volume",
			sortAscVariations: null,
			selectedFiltersVariations: null,
			
			// VAT table configuration
			tableHeadingsVat: [
				{
					text: this.getString('vat_rate'),
					fieldName: 'name',
					sortable: true,
					cols: 2,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('net_volume'),
					fieldName: 'readable_net_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('vat_amount'),
					fieldName: 'readable_vat_amount',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('units_sold'),
					fieldName: 'units_sold',
					sortable: true,
					cols: 1,
				},
			],
			filtersVat: [
				{
					type: "quantity",
					key: "units_sold",
					text: this.getString('units_sold'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			pageSizeVat: 10,
			currentPageVat: 1,
			sortFieldVat: "vat",
			sortAscVat: true,
			selectedFiltersVat: null,
		};
	},
	computed: {
		aggregatedData() {
			return this.analyticsStore.productsAggregated || {};
		},
		// Create Maps for efficient lookups
		categoryMenuMap() {
			const map = new Map();
			if (this.aggregatedData.categories_metadata) {
				this.aggregatedData.categories_metadata.forEach(cat => {
					map.set(cat.menu_category_id, cat.menu_id);
				});
			}
			return map;
		},
		productCategoryMap() {
			const map = new Map();
			if (this.aggregatedData.products_metadata) {
				this.aggregatedData.products_metadata.forEach(product => {
					map.set(product.menu_item_id, product.category_id);
				});
			}
			return map;
		},
		shouldFilterByMenu() {
			const menuStore = useMenuStore();
			return this.analyticsStore.activeMenus && 
				   this.analyticsStore.activeMenus.length > 0 && 
				   this.analyticsStore.activeMenus.length < Object.keys(menuStore.menus).length;
		},
		activeMenusSet() {
			return new Set(this.analyticsStore.activeMenus || []);
		},
		defaultVat() {
			// Get the current business country
			const country = this.userStore.currBusiness?.country || 'IT';
			// Get the VAT rates for the country, default to Italy if not found
			const countryVats = this.epsonStore.countryVats[country] || this.epsonStore.countryVats['IT'];
			// Return the first VAT rate as default, parsing it to get numeric value
			return parseFloat(countryVats[0]) || 10;
		},
		parsedProducts() {
			const products = new Map();
			const categories = new Map();
			const variations = new Map();
			const vat = new Map();
			
			// Process products data
			if (this.aggregatedData.products) {
				this.aggregatedData.products
					.filter(row => {
						const date = new Date(row.hour_bucket || row.day_bucket || row.month_bucket);
						let dateMatch;
						if (row.hour_bucket) {
							const adjustedEndDate = new Date(this.dates.end);
							adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
							dateMatch = date >= this.dates.start && date <= adjustedEndDate;
						} else {
							dateMatch = date >= this.dates.start && date <= this.dates.end;
						}
						
						// Apply menu filtering if needed
						if (dateMatch && this.shouldFilterByMenu && row.item_id) {
							const categoryId = this.productCategoryMap.get(row.item_id);
							const menuId = categoryId ? this.categoryMenuMap.get(categoryId) : null;
							return menuId && this.activeMenusSet.has(menuId);
						}
						
						return dateMatch;
					})
					.forEach(row => {
						if (row.item_id) {
							if (!products.has(row.item_id)) {
								const metadata = this.aggregatedData.products_metadata?.find(p => p.menu_item_id === row.item_id);
								products.set(row.item_id, {
									item_id: row.item_id,
									name: metadata?.name || `Product ${row.item_id}`,
									units_sold: 0,
									gross_volume: 0,
									net_volume: 0,
								});
							}
							const product = products.get(row.item_id);
							product.units_sold += row.units_sold || 0;
							product.gross_volume += (row.gross_sales || 0) / 100;
							// Use actual net revenue from backend (VAT excluded)
							product.net_volume += (row.net_sales || 0) / 100;
						}
					});
			}
			
			// Process categories data
			if (this.aggregatedData.categories) {
				this.aggregatedData.categories
					.filter(row => {
						const date = new Date(row.hour_bucket || row.day_bucket || row.month_bucket);
						let dateMatch;
						if (row.hour_bucket) {
							const adjustedEndDate = new Date(this.dates.end);
							adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
							dateMatch = date >= this.dates.start && date <= adjustedEndDate;
						} else {
							dateMatch = date >= this.dates.start && date <= this.dates.end;
						}
						
						// Apply menu filtering if needed
						if (dateMatch && this.shouldFilterByMenu && row.category_id !== null && row.category_id !== undefined) {
							const menuId = this.categoryMenuMap.get(row.category_id);
							return menuId && this.activeMenusSet.has(menuId);
						}
						
						return dateMatch;
					})
					.forEach(row => {
						if (row.category_id !== null && row.category_id !== undefined) {
							if (!categories.has(row.category_id)) {
								const metadata = this.aggregatedData.categories_metadata?.find(c => c.menu_category_id === row.category_id);
								categories.set(row.category_id, {
									category_id: row.category_id,
									name: metadata?.name || `Category ${row.category_id}`,
									units_sold: 0,
									gross_volume: 0,
									net_volume: 0,
								});
							}
							const category = categories.get(row.category_id);
							category.units_sold += row.units_sold || 0;
							category.gross_volume += (row.gross_sales || 0) / 100;
							// Use actual net revenue from backend (VAT excluded)
							category.net_volume += (row.net_sales || 0) / 100;
						}
					});
			}
			
			// Process variations data
			if (this.aggregatedData.variations) {
				this.aggregatedData.variations
					.filter(row => {
						const date = new Date(row.hour_bucket || row.day_bucket || row.month_bucket);
						let dateMatch;
						if (row.hour_bucket) {
							const adjustedEndDate = new Date(this.dates.end);
							adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
							dateMatch = date >= this.dates.start && date <= adjustedEndDate;
						} else {
							dateMatch = date >= this.dates.start && date <= this.dates.end;
						}
						
						// Apply menu filtering if needed
						if (dateMatch && this.shouldFilterByMenu && row.menu_item_list_product_id) {
							const variationMeta = this.aggregatedData.variations_metadata?.find(v => v.menu_item_list_product_id === row.menu_item_list_product_id);
							if (variationMeta && variationMeta.menu_item_id) {
								const categoryId = this.productCategoryMap.get(variationMeta.menu_item_id);
								const menuId = categoryId ? this.categoryMenuMap.get(categoryId) : null;
								return menuId && this.activeMenusSet.has(menuId);
							}
							return false;
						}
						
						return dateMatch;
					})
					.forEach(row => {
						if (row.menu_item_list_product_id) {
							if (!variations.has(row.menu_item_list_product_id)) {
								const metadata = this.aggregatedData.variations_metadata?.find(v => v.menu_item_list_product_id === row.menu_item_list_product_id);
								variations.set(row.menu_item_list_product_id, {
									menu_item_list_product_id: row.menu_item_list_product_id,
									name: metadata?.product_name || `Variation ${row.menu_item_list_product_id}`,
									units_sold: 0,
									gross_volume: 0,
								});
							}
							const variation = variations.get(row.menu_item_list_product_id);
							variation.units_sold += row.units_sold || 0;
							variation.gross_volume += (row.gross_sales || 0) / 100;
						}
					});
			}
			
			// Process VAT data
			if (this.aggregatedData.vat) {
				this.aggregatedData.vat
					.filter(row => {
						const date = new Date(row.hour_bucket || row.day_bucket || row.month_bucket);
						if (row.hour_bucket) {
							const adjustedEndDate = new Date(this.dates.end);
							adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
							return date >= this.dates.start && date <= adjustedEndDate;
						}
						return date >= this.dates.start && date <= this.dates.end;
					})
					.forEach(row => {
						// Use default VAT if row.vat is null
						const vatValue = row.vat !== null && row.vat !== undefined ? row.vat : this.defaultVat;
						
						if (!vat.has(vatValue)) {
							vat.set(vatValue, {
								vat: vatValue,
								name: `${vatValue}%`,
								units_sold: 0,
								gross_volume: 0,
								net_volume: 0,
								vat_amount: 0,
							});
						}
						const vatRow = vat.get(vatValue);
						vatRow.units_sold += row.units_sold || 0;
						vatRow.gross_volume += (row.gross_sales || 0) / 100;
						// Calculate net and VAT amount
						const grossValue = (row.gross_sales || 0) / 100;
						const vatRate = vatValue / 100;
						const netValue = grossValue / (1 + vatRate);
						vatRow.net_volume += netValue;
						vatRow.vat_amount += (grossValue - netValue);
					});
			}
			
			// Convert maps to arrays and add readable values
			let productsArray = Array.from(products.values()).map(p => ({
				...p,
				readable_gross_volume: this.currencyStore.readableCurrencyValue(p.gross_volume, false),
				readable_net_volume: this.currencyStore.readableCurrencyValue(p.net_volume, false),
			}));
			
			let categoriesArray = Array.from(categories.values()).map(c => ({
				...c,
				readable_gross_volume: this.currencyStore.readableCurrencyValue(c.gross_volume, false),
				readable_net_volume: this.currencyStore.readableCurrencyValue(c.net_volume, false),
			}));
			
			let variationsArray = Array.from(variations.values()).map(v => ({
				...v,
				readable_gross_volume: this.currencyStore.readableCurrencyValue(v.gross_volume, false),
			}));
			
			let vatArray = Array.from(vat.values()).map(v => ({
				...v,
				readable_gross_volume: this.currencyStore.readableCurrencyValue(v.gross_volume, false),
				readable_net_volume: this.currencyStore.readableCurrencyValue(v.net_volume, false),
				readable_vat_amount: this.currencyStore.readableCurrencyValue(v.vat_amount, false),
			}));
			
			// Apply filters and sorting
			productsArray = this.filterProducts(productsArray);
			categoriesArray = this.filterCategories(categoriesArray);
			variationsArray = this.filterVariations(variationsArray);
			vatArray = this.filterVat(vatArray);
			
			// Sort products
			if (this.sortFieldProducts) {
				let sortField = this.sortFieldProducts.startsWith('readable_') ? this.sortFieldProducts.replace('readable_', '') : this.sortFieldProducts;
				if (!this.sortAscProducts) {
					if (sortField === "name") {
						productsArray.sort((b, a) => a.name.localeCompare(b.name));
					} else {
						productsArray.sort((b, a) => a[sortField] - b[sortField]);
					}
				} else {
					if (sortField === "name") {
						productsArray.sort((a, b) => a.name.localeCompare(b.name));
					} else {
						productsArray.sort((a, b) => a[sortField] - b[sortField]);
					}
				}
			}
			
			// Sort categories
			if (this.sortFieldCategories) {
				let sortField = this.sortFieldCategories.startsWith('readable_') ? this.sortFieldCategories.replace('readable_', '') : this.sortFieldCategories;
				if (!this.sortAscCategories) {
					if (sortField === "name") {
						categoriesArray.sort((b, a) => a.name.localeCompare(b.name));
					} else {
						categoriesArray.sort((b, a) => a[sortField] - b[sortField]);
					}
				} else {
					if (sortField === "name") {
						categoriesArray.sort((a, b) => a.name.localeCompare(b.name));
					} else {
						categoriesArray.sort((a, b) => a[sortField] - b[sortField]);
					}
				}
			}
			
			// Sort variations
			if (this.sortFieldVariations) {
				let sortField = this.sortFieldVariations.startsWith('readable_') ? this.sortFieldVariations.replace('readable_', '') : this.sortFieldVariations;
				if (!this.sortAscVariations) {
					if (sortField === "name") {
						variationsArray.sort((b, a) => a.name.localeCompare(b.name));
					} else {
						variationsArray.sort((b, a) => a[sortField] - b[sortField]);
					}
				} else {
					if (sortField === "name") {
						variationsArray.sort((a, b) => a.name.localeCompare(b.name));
					} else {
						variationsArray.sort((a, b) => a[sortField] - b[sortField]);
					}
				}
			}
			
			// Sort VAT
			if (this.sortFieldVat) {
				let sortField = this.sortFieldVat.startsWith('readable_') ? this.sortFieldVat.replace('readable_', '') : this.sortFieldVat;
				if (!this.sortAscVat) {
					if (sortField === "name" || sortField === "vat") {
						vatArray.sort((b, a) => a.vat - b.vat);
					} else {
						vatArray.sort((b, a) => a[sortField] - b[sortField]);
					}
				} else {
					if (sortField === "name" || sortField === "vat") {
						vatArray.sort((a, b) => a.vat - b.vat);
					} else {
						vatArray.sort((a, b) => a[sortField] - b[sortField]);
					}
				}
			}
			
			return {
				products: productsArray,
				categories: categoriesArray,
				variations: variationsArray,
				vat: vatArray,
			};
		},
		currentShownProducts() {
			return this.parsedProducts.products.slice(
				(this.currentPageProducts - 1) * this.pageSizeProducts,
				this.currentPageProducts * this.pageSizeProducts
			);
		},
		currentShownCategories() {
			return this.parsedProducts.categories.slice(
				(this.currentPageCategories - 1) * this.pageSizeCategories,
				this.currentPageCategories * this.pageSizeCategories
			);
		},
		currentShownVariations() {
			return this.parsedProducts.variations.slice(
				(this.currentPageVariations - 1) * this.pageSizeVariations,
				this.currentPageVariations * this.pageSizeVariations
			);
		},
		currentShownVat() {
			return this.parsedProducts.vat.slice(
				(this.currentPageVat - 1) * this.pageSizeVat,
				this.currentPageVat * this.pageSizeVat
			);
		},
	},
	methods: {
		filterProducts(productsArray) {
			if (!productsArray || !productsArray.length || !this.selectedFiltersProducts || !Object.values(this.selectedFiltersProducts).length) {
				return productsArray;
			}
			for (let filterField in this.selectedFiltersProducts) {
				let filterValue = this.selectedFiltersProducts[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if (filterValue && ['gross_volume', 'net_volume', 'units_sold'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						productsArray = productsArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						productsArray = productsArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						productsArray = productsArray.filter(b => b[filterName] === finalValue);
					}
				}
			}
			return productsArray;
		},
		filterCategories(categoriesArray) {
			if (!categoriesArray || !categoriesArray.length || !this.selectedFiltersCategories || !Object.values(this.selectedFiltersCategories).length) {
				return categoriesArray;
			}
			for (let filterField in this.selectedFiltersCategories) {
				let filterValue = this.selectedFiltersCategories[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if (filterValue && ['gross_volume', 'net_volume', 'units_sold'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						categoriesArray = categoriesArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						categoriesArray = categoriesArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						categoriesArray = categoriesArray.filter(b => b[filterName] === finalValue);
					}
				}
			}
			return categoriesArray;
		},
		filterVariations(variationsArray) {
			if (!variationsArray || !variationsArray.length || !this.selectedFiltersVariations || !Object.values(this.selectedFiltersVariations).length) {
				return variationsArray;
			}
			for (let filterField in this.selectedFiltersVariations) {
				let filterValue = this.selectedFiltersVariations[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if (filterValue && ['gross_volume', 'units_sold'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						variationsArray = variationsArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						variationsArray = variationsArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						variationsArray = variationsArray.filter(b => b[filterName] === finalValue);
					}
				}
			}
			return variationsArray;
		},
		filterVat(vatArray) {
			if (!vatArray || !vatArray.length || !this.selectedFiltersVat || !Object.values(this.selectedFiltersVat).length) {
				return vatArray;
			}
			for (let filterField in this.selectedFiltersVat) {
				let filterValue = this.selectedFiltersVat[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if (filterValue && ['gross_volume', 'units_sold'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						vatArray = vatArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						vatArray = vatArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						vatArray = vatArray.filter(b => b[filterName] === finalValue);
					}
				}
			}
			return vatArray;
		},
		changeSortProducts(newSortField, newSortAsc) {
			this.currentPageProducts = 1;
			this.sortFieldProducts = newSortField;
			this.sortAscProducts = !!newSortAsc;
			this.$refs['products-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeFilterProducts(newFilters) {
			this.currentPageProducts = 1;
			this.selectedFiltersProducts = newFilters;
			this.$refs['products-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeSortCategories(newSortField, newSortAsc) {
			this.currentPageCategories = 1;
			this.sortFieldCategories = newSortField;
			this.sortAscCategories = !!newSortAsc;
			this.$refs['categories-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeFilterCategories(newFilters) {
			this.currentPageCategories = 1;
			this.selectedFiltersCategories = newFilters;
			this.$refs['categories-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeSortVariations(newSortField, newSortAsc) {
			this.currentPageVariations = 1;
			this.sortFieldVariations = newSortField;
			this.sortAscVariations = !!newSortAsc;
			this.$refs['variations-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeFilterVariations(newFilters) {
			this.currentPageVariations = 1;
			this.selectedFiltersVariations = newFilters;
			this.$refs['variations-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeSortVat(newSortField, newSortAsc) {
			this.currentPageVat = 1;
			this.sortFieldVat = newSortField;
			this.sortAscVat = !!newSortAsc;
			this.$refs['vat-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeFilterVat(newFilters) {
			this.currentPageVat = 1;
			this.selectedFiltersVat = newFilters;
			this.$refs['vat-table'].$refs['pagination-btns'].activePage = 1;
		},
		exportProducts(expExtension) {
			let nameKey = this.getString('name');
			let grossVolumeKey = this.getString('gross_volume') + " " + this.currencyStore.currCurrency.symbol;
			let netVolumeKey = this.getString('net_volume') + " " + this.currencyStore.currCurrency.symbol;
			let unitsSoldKey = this.getString('units_sold');
			let data = this.parsedProducts.products.map(p => {
				let result = {};
				result[nameKey] = p.name;
				result[grossVolumeKey] = Number(p.gross_volume.toFixed(2)).toLocaleString(["it-IT"]);
				result[netVolumeKey] = Number(p.net_volume.toFixed(2)).toLocaleString(["it-IT"]);
				result[unitsSoldKey] = p.units_sold;
				return result;
			});
			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_" + this.getString("products").toLowerCase() + "_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;

			if (data.length > 0) {
				this.scriptStore.loadScript('xlsx', () => {
					const worksheet = window.XLSX.utils.json_to_sheet(data);
					const workbook = window.XLSX.utils.book_new();
					window.XLSX.utils.book_append_sheet(workbook, worksheet, this.getString("products"));

					window.XLSX.utils.sheet_add_aoa(worksheet, [[nameKey, grossVolumeKey, netVolumeKey, unitsSoldKey]], { origin: "A1" });

					if (expExtension === "xlsx") {
						window.XLSX.writeFileXLSX(workbook, fileName + ".xlsx");
					}
					else {
						window.XLSX.writeFile(workbook, fileName + ".csv");
					}
				});
			}
			else {
				this.showNotification('table-no-data');
			}
		},
		exportCategories(expExtension) {
			let nameKey = this.getString('name');
			let grossVolumeKey = this.getString('gross_volume') + " " + this.currencyStore.currCurrency.symbol;
			let netVolumeKey = this.getString('net_volume') + " " + this.currencyStore.currCurrency.symbol;
			let unitsSoldKey = this.getString('units_sold');
			let data = this.parsedProducts.categories.map(c => {
				let result = {};
				result[nameKey] = c.name;
				result[grossVolumeKey] = Number(c.gross_volume.toFixed(2)).toLocaleString(["it-IT"]);
				result[netVolumeKey] = Number(c.net_volume.toFixed(2)).toLocaleString(["it-IT"]);
				result[unitsSoldKey] = c.units_sold;
				return result;
			});
			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_" + this.getString("categories").toLowerCase() + "_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;

			if (data.length > 0) {
				this.scriptStore.loadScript('xlsx', () => {
					const worksheet = window.XLSX.utils.json_to_sheet(data);
					const workbook = window.XLSX.utils.book_new();
					window.XLSX.utils.book_append_sheet(workbook, worksheet, this.getString("categories"));

					window.XLSX.utils.sheet_add_aoa(worksheet, [[nameKey, grossVolumeKey, netVolumeKey, unitsSoldKey]], { origin: "A1" });

					if (expExtension === "xlsx") {
						window.XLSX.writeFileXLSX(workbook, fileName + ".xlsx");
					}
					else {
						window.XLSX.writeFile(workbook, fileName + ".csv");
					}
				});
			}
			else {
				this.showNotification('table-no-data');
			}
		},
		exportVariations(expExtension) {
			let nameKey = this.getString('name');
			let grossVolumeKey = this.getString('gross_volume') + " " + this.currencyStore.currCurrency.symbol;
			let unitsSoldKey = this.getString('units_sold');
			let data = this.parsedProducts.variations.map(v => {
				let result = {};
				result[nameKey] = v.name;
				result[grossVolumeKey] = Number(v.gross_volume.toFixed(2)).toLocaleString(["it-IT"]);
				result[unitsSoldKey] = v.units_sold;
				return result;
			});
			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_" + this.getString("variations").toLowerCase() + "_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;

			if (data.length > 0) {
				this.scriptStore.loadScript('xlsx', () => {
					const worksheet = window.XLSX.utils.json_to_sheet(data);
					const workbook = window.XLSX.utils.book_new();
					window.XLSX.utils.book_append_sheet(workbook, worksheet, this.getString("variations"));

					window.XLSX.utils.sheet_add_aoa(worksheet, [[nameKey, grossVolumeKey, unitsSoldKey]], { origin: "A1" });

					if (expExtension === "xlsx") {
						window.XLSX.writeFileXLSX(workbook, fileName + ".xlsx");
					}
					else {
						window.XLSX.writeFile(workbook, fileName + ".csv");
					}
				});
			}
			else {
				this.showNotification('table-no-data');
			}
		},
		exportVat(expExtension) {
			let vatRateKey = this.getString('vat_rate');
			let grossVolumeKey = this.getString('gross_volume') + " " + this.currencyStore.currCurrency.symbol;
			let netVolumeKey = this.getString('net_volume') + " " + this.currencyStore.currCurrency.symbol;
			let vatAmountKey = this.getString('vat_amount') + " " + this.currencyStore.currCurrency.symbol;
			let unitsSoldKey = this.getString('units_sold');
			let data = this.parsedProducts.vat.map(v => {
				let result = {};
				result[vatRateKey] = v.name;
				result[grossVolumeKey] = Number(v.gross_volume.toFixed(2)).toLocaleString(["it-IT"]);
				result[netVolumeKey] = Number(v.net_volume.toFixed(2)).toLocaleString(["it-IT"]);
				result[vatAmountKey] = Number(v.vat_amount.toFixed(2)).toLocaleString(["it-IT"]);
				result[unitsSoldKey] = v.units_sold;
				return result;
			});
			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_" + this.getString("vat_breakdown").toLowerCase() + "_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;

			if (data.length > 0) {
				this.scriptStore.loadScript('xlsx', () => {
					const worksheet = window.XLSX.utils.json_to_sheet(data);
					const workbook = window.XLSX.utils.book_new();
					window.XLSX.utils.book_append_sheet(workbook, worksheet, this.getString("vat_breakdown"));

					window.XLSX.utils.sheet_add_aoa(worksheet, [[vatRateKey, grossVolumeKey, netVolumeKey, vatAmountKey, unitsSoldKey]], { origin: "A1" });

					if (expExtension === "xlsx") {
						window.XLSX.writeFileXLSX(workbook, fileName + ".xlsx");
					}
					else {
						window.XLSX.writeFile(workbook, fileName + ".csv");
					}
				});
			}
			else {
				this.showNotification('table-no-data');
			}
		},
	},
}
</script>

<style scoped>

</style>